import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import "./App.css";
// Foundry skin for the /admin console. Scoped to the `.foundry` wrapper that
// AdminShell renders, so the public events pages are untouched. Imported after
// index.css/App.css so its bridge rules win over index.css's @layer components.
import "./styles/foundry.css";
import { bridgeSession, clearSession } from "./lib/register";
import { AuthProvider } from "./lib/auth";
import { ThemeProvider } from "./lib/theme";
import ProtectedRoute from "./components/ProtectedRoute";

import Login from "./pages/Login";
import SsoCallback from "./pages/SsoCallback";
import Events from "./pages/Events";
import EventDetail from "./pages/EventDetail";
import CapitalSeriesPage from "./pages/CapitalSeriesPage";
import StandaloneEvent from "./pages/StandaloneEvent";
import FeedbackForm from "./pages/FeedbackForm";
import CancelBooking from "./pages/CancelBooking";
import Unsubscribe from "./pages/Unsubscribe";
import MyEvents from "./pages/MyEvents";

import AdminEvents from "./pages/admin/AdminEvents";
import AdminEventCompose from "./pages/admin/AdminEventCompose";
import AdminEventManage from "./pages/admin/AdminEventManage";
import AdminEventCheckin from "./pages/admin/AdminEventCheckin";
import AdminSessions from "./pages/admin/AdminSessions";
import AdminRequests from "./pages/admin/AdminRequests";
import AdminPrograms from "./pages/admin/AdminPrograms";
import AdminSeriesEdit from "./pages/admin/AdminSeriesEdit";
import AdminCommunity from "./pages/admin/AdminCommunity";
import AdminEventAnalytics from "./pages/admin/AdminEventAnalytics";
import AdminIntegrations from "./pages/admin/AdminIntegrations";

// Clears the short verified session (the "logged in" state) and returns to events.
function Logout() {
  const navigate = useNavigate();
  useEffect(() => {
    clearSession();
    toast.success("Signed out");
    navigate("/events", { replace: true });
  }, [navigate]);
  return null;
}

function App() {
  // Silent member SSO: a members-portal session becomes an events session on
  // first load (no-op without the shared cookie; see bridgeSession).
  useEffect(() => { void bridgeSession(); }, []);
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" toastOptions={{ style: { background: "rgba(255,255,255,0.9)", backdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.6)", color: "hsl(220 15% 12%)", boxShadow: "0 10px 30px -10px rgba(22,24,29,0.14)" } }} />
          <Routes>
            {/* App is Events-only. / redirects to /events. */}
            <Route path="/" element={<Navigate to="/events" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/sso" element={<SsoCallback />} />

            <Route path="/events" element={<Events />} />
            <Route path="/my-events" element={<MyEvents />} />
            <Route path="/events/capital/:id" element={<CapitalSeriesPage />} />
            <Route path="/events/:id" element={<EventDetail />} />
            <Route path="/e/:token" element={<StandaloneEvent />} />
            <Route path="/feedback/:token" element={<FeedbackForm />} />
            <Route path="/cancel/:token" element={<CancelBooking />} />
            <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
            <Route path="/logout" element={<Logout />} />

            {/* Admin */}
            <Route path="/admin" element={<Navigate to="/admin/community" replace />} />
            <Route path="/admin/community" element={<ProtectedRoute role="admin"><AdminCommunity /></ProtectedRoute>} />
            <Route path="/admin/community/events/:id" element={<ProtectedRoute role="admin"><AdminEventAnalytics /></ProtectedRoute>} />
            <Route path="/admin/events" element={<ProtectedRoute role="admin"><AdminEvents /></ProtectedRoute>} />
            <Route path="/admin/events/new" element={<ProtectedRoute role="admin"><AdminEventCompose /></ProtectedRoute>} />
            <Route path="/admin/events/:id/edit" element={<ProtectedRoute role="admin"><AdminEventCompose /></ProtectedRoute>} />
            <Route path="/admin/events/:id/manage" element={<ProtectedRoute role="admin"><AdminEventManage /></ProtectedRoute>} />
            <Route path="/admin/events/:id/checkin" element={<ProtectedRoute role="admin"><AdminEventCheckin /></ProtectedRoute>} />
            <Route path="/admin/sessions" element={<ProtectedRoute role="admin"><AdminRequests /></ProtectedRoute>} />
            {/* Legacy per-series queue, kept reachable during the transition */}
            <Route path="/admin/sessions-legacy" element={<ProtectedRoute role="admin"><AdminSessions /></ProtectedRoute>} />
            <Route path="/admin/programs" element={<ProtectedRoute role="admin"><AdminPrograms /></ProtectedRoute>} />
            <Route path="/admin/series/:id/edit" element={<ProtectedRoute role="admin"><AdminSeriesEdit /></ProtectedRoute>} />
            <Route path="/admin/integrations" element={<ProtectedRoute role="admin"><AdminIntegrations /></ProtectedRoute>} />

            {/* Catch-all → events */}
            <Route path="*" element={<Navigate to="/events" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
